"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useAuth } from "@/components/auth-provider";
import { useRouter, useSearchParams } from "next/navigation";
import { BrandIcon, BrandWordmark } from "@/components/brand";
import { useT } from "@/i18n/provider";
import { EnglishMarketingScope } from "@/components/english-scope";
import { safeReturnToPath } from "@/lib/safe-return-to";


// Suspense boundary required by Next 14: any component that calls
// useSearchParams() bails out of static prerender unless it's wrapped.
// Public paths now render server-side (see app-shell.tsx), so this page
// actually goes through prerender and needs the boundary — pre-fix the
// AppShell loading splash masked the issue at build time.
export default function LoginPage() {
  return (
    <EnglishMarketingScope>
      <Suspense fallback={null}>
        <LoginPageBody />
      </Suspense>
    </EnglishMarketingScope>
  );
}

function LoginPageBody() {
  const t = useT();
  const { login, completeTwoFactor } = useAuth();
  const router = useRouter();
  const params = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // 2FA challenge step: password accepted, waiting for a TOTP/recovery code.
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  // When the middleware kicks us here with `?reason=session_expired`, show
  // a one-line notice so the user understands *why* they're re-logging.
  const reason = params?.get("reason") ?? null;
  const next = params?.get("next") ?? null;
  const reasonMessage =
    reason === "session_expired"
      ? t("login.sessionExpired")
      // Arrived from a page that needs a login and had no session at all —
      // a different fact from "your session expired", and saying the wrong
      // one tells a first-time visitor they were signed out of something
      // they never signed in to.
      : reason === "sign_in_required"
        ? t("login.signInRequired")
        : null;

  // `@/lib/safe-return-to` is the one authority: a single leading slash is not
  // a rule — `?next=/\\evil.example` resolves to another host, which would
  // make a successful login land the user on a phishing page.
  const destAfterLogin = () => safeReturnToPath(next) ?? "/app";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);

    const result = await login(email, password);
    if (result.ok) {
      // Setup-limited session: policy requires 2FA before anything else.
      // Honour the `next=/path` hint the middleware set so users land back
      // where they were interrupted.
      router.replace(result.setupRequired ? "/two-factor-setup" : destAfterLogin());
    } else if (result.twoFactorRequired) {
      setTwoFactorStep(true);
    } else {
      setError(result.error ?? t("login.loginFailed"));
    }
    setBusy(false);
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const result = await completeTwoFactor(code.trim());
    if (result.ok) {
      router.replace(destAfterLogin());
    } else if (result.code === "challenge_expired") {
      // Challenge timed out or ran out of attempts — back to the password step.
      setTwoFactorStep(false);
      setCode("");
      setPassword("");
      setError(t("login.twoFactor.expired"));
    } else {
      setError(result.error ?? t("login.twoFactor.badCode"));
    }
    setBusy(false);
  };

  const inputCls =
    "w-full rounded-lg border border-line-subtle bg-bg-base px-4 py-2.5 text-[14px] text-fg placeholder:text-fg-muted focus:outline-none focus:border-brand-400/50 focus:ring-2 focus:ring-brand-500/20";

  return (
    <main className="min-h-screen grid-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / brand — clickable, returns to the public landing page */}
        <div className="text-center mb-8">
          <Link
            href="/"
            aria-label={t("login.backToHomeAria")}
            className="inline-flex flex-col items-center gap-4 rounded-xl px-3 py-2 -mx-3 -my-2 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 transition"
          >
            <BrandIcon size={32} />
            <h1 className="tracking-tight">
              <BrandWordmark className="text-[22px]" />
            </h1>
          </Link>
          <p className="text-[13px] text-fg-muted mt-1">{t("login.subtitle")}</p>
        </div>

        {reasonMessage && (
          <div className="mb-3 rounded-lg border border-status-info/30 bg-status-info/10 text-status-info text-[12px] px-3 py-2">
            {reasonMessage}
          </div>
        )}

        {/* 2FA challenge step — password accepted, second factor pending */}
        {twoFactorStep ? (
          <form onSubmit={submitCode} className="card p-5 space-y-4">
            <div>
              <h2 className="text-[14px] font-semibold text-fg-bright">{t("login.twoFactor.title")}</h2>
              <p className="text-[12px] text-fg-muted mt-1">
                {useRecovery ? t("login.twoFactor.recoveryHint") : t("login.twoFactor.codeHint")}
              </p>
            </div>

            <div>
              <label className="text-[11px] text-fg-muted mb-1.5 block">
                {useRecovery ? t("login.twoFactor.recoveryLabel") : t("login.twoFactor.codeLabel")}
              </label>
              <input
                type="text"
                inputMode={useRecovery ? "text" : "numeric"}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={useRecovery ? "XXXX-XXXX-XXXX-XXXX" : "123456"}
                className={inputCls}
                autoFocus
                required
              />
            </div>

            {error && (
              <div className="rounded-lg bg-status-critical/10 border border-status-critical/20 px-3 py-2 text-[12px] text-status-critical">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !code.trim()}
              className="w-full py-2.5 rounded-lg bg-brand-grad text-white text-[14px] font-medium shadow-glow hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? t("login.twoFactor.verifying") : t("login.twoFactor.verify")}
            </button>

            <div className="flex items-center justify-between text-[12px]">
              <button
                type="button"
                onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}
                className="text-brand-300 hover:text-brand-200 underline-offset-2 hover:underline"
              >
                {useRecovery ? t("login.twoFactor.useAuthenticator") : t("login.twoFactor.useRecovery")}
              </button>
              <button
                type="button"
                onClick={() => { setTwoFactorStep(false); setCode(""); setError(""); }}
                className="text-fg-muted hover:text-fg underline-offset-2 hover:underline"
              >
                {t("login.twoFactor.back")}
              </button>
            </div>
          </form>
        ) : (
        <form onSubmit={submit} className="card p-5 space-y-4">
          <div>
            <label className="text-[11px] text-fg-muted mb-1.5 block">{t("login.emailLabel")}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.emailPlaceholder")}
              className={inputCls}
              autoFocus
              required
            />
          </div>

          <div>
            <label className="text-[11px] text-fg-muted mb-1.5 block">{t("login.passwordLabel")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.passwordPlaceholder")}
              className={inputCls}
              required
            />
          </div>

          <div className="-mt-1 text-right">
            <Link
              href="/forgot-password"
              className="text-[12px] text-brand-300 hover:text-brand-200 underline-offset-2 hover:underline"
            >
              {t("login.forgotPassword")}
            </Link>
          </div>

          {error && (
            <div className="rounded-lg bg-status-critical/10 border border-status-critical/20 px-3 py-2 text-[12px] text-status-critical">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full py-2.5 rounded-lg bg-brand-grad text-white text-[14px] font-medium shadow-glow hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? t("login.signingIn") : t("login.signIn")}
          </button>
        </form>
        )}

        <p className="text-center text-[12px] text-fg-muted mt-4">
          {t("login.noAccountPrompt")}{" "}
          <Link
            href="/signup"
            className="text-brand-300 hover:text-brand-200 font-medium underline-offset-2 hover:underline"
          >
            {t("login.signUpFree")}
          </Link>
        </p>
      </div>
    </main>
  );
}
